import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from '../utils/getEntities';
import {
  CourseProgressLogType,
  CourseProgressStatus,
  ICourseProgress,
  ICourseProgressDAO,
  ICourseProgressLog,
} from 'types/domain/permissions';
import { ISearch, SortBy } from 'types/domain/search';
import { parseUserDAO } from './users';
import { convertDate } from 'utils/convertDate';
import { reduceArrayById } from 'utils/reduceArrayById';
import { IReportReducer } from 'core/slices/report';
import { parseGroupUser } from './groupUsers';
import { createCachedSelector } from 're-reselect';
import { parseModule } from '../product/module';
import { IOrgUserCertificate, IOrgUserVariable, IOrgUserVariableDAO } from 'types/domain/user';
import { ISessionAttempt } from 'types/domain/session';
import { IEmail } from 'types/domain/notifications';
import { parseVariableDAO } from './variable';
import { parseCourseNodeDAO } from './courseNodes';
import { createSelector } from 'reselect';
import { COMPONENT_ID_ADD_LEARNER } from 'app/features/Module/ModuleShare/ModuleShare';
import { ISearchReducer } from 'core/slices/search';
import state from 'core/reducers/state';

const entity = (state: RootState) => state.entity.present;
const courseId = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.COURSE]);
const courseProgressId = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.COURSE_PROGRESS]);
const search = (state: RootState) => state.core.search;
const report = (state: RootState) => state.core.report;

export const selectCourseProgressList = createCachedSelector(
  [entity, courseId, search, report],
  (entity: IEntityReducer, id: number, search: ISearch, report: IReportReducer) => {
    return getEntities(entity, EntityTypes.COURSE_PROGRESS, 'courseId', id)
      .filter((courseProgress: ICourseProgress) => courseProgress.status > CourseProgressStatus.NOT_STARTED)
      .map((courseProgress: ICourseProgress) => parseCourseProgress(entity, courseProgress.id));
  },
)((state, id) => id || state.state[EntityTypes.COURSE] || 'unknown');

export const selectCourseProgressName = createCachedSelector(
  [entity, courseId, search, report],
  (entity: IEntityReducer, id: number, search: ISearchReducer, report: IReportReducer) => {
    const term = search.components[COMPONENT_ID_ADD_LEARNER]?.searchTerm;
    return getEntities(entity, EntityTypes.COURSE_PROGRESS, 'courseId', id)
      .filter((courseProgress: ICourseProgress) => !!courseProgress.organisationUserId)
      .map((courseProgress: ICourseProgress) => parseCourseProgress(entity, courseProgress.id))

      .filter(cp =>
        term && term.length
          ? cp.organisationUser.name?.toLowerCase().includes(search.term.toLowerCase()) ||
            cp.organisationUser.email?.toLowerCase().includes(search.term.toLowerCase())
          : true,
      );
  },
)((state, id) => id || state.state[EntityTypes.COURSE] || 'unknown');

export const selectCourseInvitesList = createCachedSelector(
  [entity, courseId, search, report],
  (entity: IEntityReducer, id: number, search: ISearch, report: IReportReducer) => {
    return getEntities(entity, EntityTypes.COURSE_PROGRESS, 'courseId', id)
      .filter((courseProgress: ICourseProgress) => courseProgress.status === CourseProgressStatus.NOT_STARTED)
      .map((courseProgress: ICourseProgress) => parseCourseProgress(entity, courseProgress.id));
  },
)((state, id) => id || state.state[EntityTypes.COURSE] || 'unknown');

// Selector for paginated course progress - uses IDs from server response
export const selectPaginatedCourseProgress = createSelector(
  [
    (state: RootState) => state.entity.present,
    (state: RootState, componentId: string) => state.core.search.components[componentId],
    (state: RootState, componentId: string, courseNodeId?) => courseNodeId,
  ],
  (entity, paginationState, courseNodeId) => {
    if (!paginationState) {
      return {
        paginatedProgress: [],
        totalRecords: 0,
        currentPage: 0,
        pageSize: 10,
        loading: false,
      };
    }

    const courseProgress = getEntities(entity, EntityTypes.COURSE_PROGRESS, 'courseNodeId', courseNodeId).filter(
      cp => !!cp.inviteUrl,
    );

    const { currentPage, pageSize, loading, totalRecords, ids } = paginationState;

    // If we have more course progress than IDs from server, add missing ones for the module share page
    let allIds = ids;
    if (courseNodeId && courseProgress.length) {
      const existingIds = new Set(ids);
      const missingIds = courseProgress.filter(cp => !existingIds.has(cp.id)).map(cp => cp.id);
      allIds = [...missingIds, ...ids].slice(0, pageSize);
    }

    // The IDs are already paginated from the server, so use them directly
    // Use the paginated IDs to get the exact records in order
    const paginatedProgress = allIds.map(id => parseCourseProgress(entity, id)).filter(progress => progress !== null);

    return {
      paginatedProgress,
      totalRecords,
      currentPage,
      pageSize,
      loading,
    };
  },
);

export const selectCourseProgress = createSelector(entity, courseProgressId, parseCourseProgress);

export function parseCourseProgress(entity, id) {
  const courseProgress: ICourseProgress = entity[EntityTypes.COURSE_PROGRESS][id];
  // const organisationUser = parseUserDAO(entity, courseProgress.organisationUserId);
  if (!courseProgress) return null;

  const organisationUserVariables = reduceArrayById(
    getEntities(entity, EntityTypes.ORG_USER_VARIABLE, 'courseProgressId', id).map((orgUserVar: IOrgUserVariable) => {
      return {
        ...orgUserVar,
        text: orgUserVar.textId ? entity[EntityTypes.TEXT][orgUserVar.textId] : null,
      } as Partial<IOrgUserVariableDAO>;
    }),
    'variableId',
  );
  return {
    ...courseProgress,
    date: courseProgress.modified?.date || courseProgress.created?.date,
    organisationUserVariables,
    organisationUser: parseUserDAO(entity, courseProgress.organisationUserId),
    lastOrganisationUserVariable: courseProgress.lastOrganisationUserVariableId
      ? entity[EntityTypes.ORG_USER_VARIABLE][courseProgress.lastOrganisationUserVariableId]
      : null,
    sessionAttempt: courseProgress.sessionAttemptId
      ? entity[EntityTypes.SESSION_ATTEMPT][courseProgress.sessionAttemptId]
      : null,
    module: courseProgress.moduleId ? parseModule(entity, courseProgress.moduleId) : null,
    lastGroupUser: courseProgress.lastGroupUserId ? parseGroupUser(entity, courseProgress.lastGroupUserId) : null,
    courseOrgUserVariable: organisationUserVariables[entity[EntityTypes.COURSE][courseProgress.courseId]?.variableId],
    lastEmail: courseProgress.lastEmailId ? entity[EntityTypes.EMAIL][courseProgress.lastEmailId] : null,
    emails: getEntities(entity, EntityTypes.EMAIL, 'courseProgressId', id),
    log: parseCourseProgressLog(entity, id, entity[EntityTypes.COURSE][courseProgress.courseId]?.variableId),
  } as ICourseProgressDAO;
}

function parseCourseProgressLog(entity, id, courseVariableId) {
  const orgUserVars = getEntities(entity, EntityTypes.ORG_USER_VARIABLE, 'courseProgressId', id)
    .filter(ouv => ouv.variableId !== courseVariableId)
    .map((orgUserVariable: IOrgUserVariable) => {
      return {
        type: CourseProgressLogType.ORG_USER_VARIABLE,
        created: orgUserVariable.created,
        modified: orgUserVariable.modified,
        orgUserVariable: {
          ...orgUserVariable,
          courseNode: parseCourseNodeDAO(entity, orgUserVariable.courseNodeId),
          text: orgUserVariable.textId ? entity[EntityTypes.TEXT][orgUserVariable.textId] : null,
          variable: parseVariableDAO(entity, orgUserVariable.variableId),
        } as IOrgUserVariableDAO,
      } as ICourseProgressLog;
    });

  return [
    ...getEntities(entity, EntityTypes.SESSION_ATTEMPT, 'courseProgressId', id).map(
      (sessionAttempt: ISessionAttempt) => {
        return {
          type: CourseProgressLogType.SESSION_ATTEMPT,
          created: sessionAttempt.created,
          modified: sessionAttempt.modified,
          sessionAttempt,
          orgUserVariable: orgUserVars.find(orv => orv.orgUserVariable.variable.moduleId === sessionAttempt.moduleId)
            ?.orgUserVariable,
        } as ICourseProgressLog;
      },
    ),
    ...orgUserVars.filter(orv => !orv.orgUserVariable.variable?.moduleId),
    ...getEntities(entity, EntityTypes.ORG_USER_CERTIFICATE, 'courseProgressId', id).map(
      (orgUserCertificate: IOrgUserCertificate) => {
        return {
          type: CourseProgressLogType.ORG_USER_CERTIFICATE,
          created: orgUserCertificate.created,
          modified: orgUserCertificate.modified,
          orgUserCertificate,
        } as ICourseProgressLog;
      },
    ),
    ...getEntities(entity, EntityTypes.EMAIL, 'courseProgressId', id).map((email: IEmail) => {
      return {
        type: CourseProgressLogType.EMAIL,
        created: email.created,
        modified: email.modified,
        email,
      } as ICourseProgressLog;
    }),
  ].sort(sortByDateModified) as ICourseProgressLog[];
}

function StatusFilter(cp: ICourseProgressDAO, status: CourseProgressStatus) {
  switch (status) {
    case CourseProgressStatus.COMPLETE:
      return !!cp.courseOrgUserVariable;
    case CourseProgressStatus.DORMANT:
    case CourseProgressStatus.ACTIVE:
      return [CourseProgressStatus.ACTIVE, CourseProgressStatus.DORMANT].includes(cp.status);
  }
}

export function sortByNameModified(a: ICourseProgressDAO, b: ICourseProgressDAO, search: ISearch) {
  switch (search.sort) {
    case SortBy.NAME:
      if (a.organisationUser.name == null) return -1;
      if (b.organisationUser.name == null) return 1;
      return a.organisationUser.name.localeCompare(b.organisationUser.name);
    default:
      if (a.date == null) return 1;
      if (b.date == null) return -1;

      return convertDate(a.date) < convertDate(b.date) ? 1 : -1;
  }
}

export function sortByDateModified(a: ICourseProgressLog, b: ICourseProgressLog) {
  const aDate = a.modified?.date || a.created?.date;
  const bDate = b.modified?.date || b.created?.date;

  if (aDate == null) return 1;
  if (bDate == null) return -1;

  return convertDate(aDate) < convertDate(bDate) ? -1 : 1;
}
